import { AppDate } from '../envTypes';
import { ModelBase } from './base';
import { Profile } from './profile';

export interface GlobalMessage {
  thumbnail?: string;
  cta?: string;
  title: string;
  message: string;
  enabled: boolean;
  createdAt: AppDate;
}

export interface DConTicket {
  token: string;
  uid: string;
  createdAt: AppDate;
  purchaseAt: AppDate;
  fromType: string;
  ticketType: string;
  status: 'init' | 'processed' | 'skipped';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra?: { [key: string]: any };
  isNewUser?: boolean;
}

export interface TempTransactionData {
  listingId: string;
  sellerId: string;
  soldQuantity: number;
  liveStreamId: string | null;
  variantId: string | null;
  isSynced: boolean;
  sellerGotAmount: number;
  isShippo: boolean;
  isDone: boolean;
  orderId: string;
}

export interface ContentSubscriber extends ModelBase {
  userId?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface SellerMarketing {
  firstName: string;
  lastName: string;
  email: string;
  instagram?: string;
  website?: string;
  phoneNumber?: string;
  createdAt: AppDate;
}

export interface DashboardReport {
  label: string;
  startAt: Date;
  startDate: string;
  endAt: Date;
  endDate: string;
  period: 'daily' | 'weekly' | 'monthly';
  type: string;
  audienceCount: number;
  newAudienceCount: number;
  firstCustomerCount: number;
  firstCustomerPercentage: number;
  newSeller: number;
  newUser: number;
  activeUser: number;
  waitlistCount: number;
  showDuration: number;
  viewedDuration: number;
  showCount: number;
  engagementCount: number;
  averageEngagement: number;
  activeSellerCount: number;
  averageShowPerAudience: number;
  averageViewedDuration: number;
  // shipping label
  shippingLabel: number;
  shippingFee: number;
  // order
  orderCount: number;
  itemPurchased: number;
  orderTotal: number;
  salesTaxAmount: number;
  buyerPaidAmount: number; // how much the buyer actually paid with refunds if any
  sellerGotAmount: number; // how much the seller actually got with refunds if any
  // customer
  customerCount: number;
  newCustomerCount: number;
  repeatCustomerCount: number;
  repeatCustomerPercentage: number;
}

export interface SellerReport {
  label?: string;
  startAt?: Date;
  endAt?: Date;
  liveStream: number;
  referral: number;
  follower: number;
  viewer: number;
  uniqViewer: number;
  newUniqViewer: number;
  comment: number;
  like: number;
  orderCount: number;
  orderTotal: number;
  sellerGotAmount: number;
  duration: number;
  viewedDuration: number;
  salesPerMinute: number;
  uniqCustomer: number;
  returnCustomer: number;
}

export enum AppGlobalDocumentID {
  PromotionPage = 'PromotionPage1',
  ScheduleNextShowPage = 'ScheduleNextShowPage',
  CrystalWeekPage = 'PromotionPage0',
  LeaveAppReview = 'LeaveAppReview',
}

export interface LeaveAppReview {
  title: string;
  message: string;
  cta: string;
  enabled: boolean;
}

export interface PromotionPage {
  video: string;
  avatar: string;
  title: string;
  coverImage: string;
  enabled: boolean;
  thumbnailNickName: string;
}

export interface DashboardFeedback {
  type: 'dashboard';
  topic?: { id: string; topic: 'order' | 'liveStream' };
  content: {
    message: string;
    extra?: string | null; // UUID
    imageUrls?: string[];
    videoUrls?: string[];
  };
  userId: string;
  status: 'initial' | 'processing' | 'finished';
  createdAt: AppDate;
  // must use signed doc id
  forceSign?: boolean;
  signedDocId?: string;
  creatorId?: string;
}

export interface AsyncTask {
  createdAt: AppDate;
  updatedAt: AppDate;
  requestPayload: string;
  responsePayload: string;
  type:
    | 'ordersLabelsDownload'
    | 'ordersPackingSlipDownload'
    | 'ordersShipmentLabelDownload';
  creatorId: string;
  status: 'notStarted' | 'inProgress' | 'completed' | 'failed';
  percentageComplete: number;
}

export interface DashboardNews extends ModelBase {
  brief: string;
  url?: string;
  active: boolean;
  startedAt?: AppDate;
  expiredAt?: AppDate;
}

export interface CommunityWaitlistUser extends ModelBase {
  profile: Profile;
}

// /featuredTags/{autoId}
export interface FeaturedTag extends ModelBase {
  name: string;

  sortedAt: AppDate;

  sellerTags: string[];
}

/**
 * Aggregated from `Listing.tags`
 *
 * @path: /sellerTags/{autoId}
 */
export interface SellerTag extends ModelBase {
  name: string;
}

// popstore creator application for selling dropship products
export interface PopStoreDropshipApplication extends ModelBase {
  creatorId: string; // popstore creator id
  creatorProfile: Profile; // popstore creator profile
  state: string; // state(i.g. NY, CA), only states in US supported now
  instagramId: string; // instagram id(i.g. @popshop.live)
  status: PopStoreDropshipApplicationStatus;
  reason?: string; // reason for approve or reject
  auditedAt?: AppDate;
  auditorId?: string;
  auditorProfile?: Profile;
}

export enum PopStoreDropshipApplicationAction {
  Approve = 'approve',
  Reject = 'reject',
}

export enum PopStoreDropshipApplicationStatus {
  Submitted = 'submitted',
  Approved = 'approved',
  Rejected = 'rejected',
}
